import React from 'react';
import { motion } from 'framer-motion';
import PhaseCard from '../components/PhaseCard';

const Implementation = () => {
  const phases = [
    {
      number: 1,
      title: "Audit & Setup",
      description: "We begin by analyzing your current workflows and identifying tasks for delegation.",
      items: [
        "Complete business systems audit",
        "Identify time-consuming tasks",
        "Document existing processes",
        "Prioritize delegation tasks",
        "Setup communication channels"
      ]
    },
    {
      number: 2,
      title: "Database Optimization",
      description: "Organizing your CRM and business data for maximum efficiency and accessibility.",
      items: [
        "CRM cleanup and organization",
        "Data entry and standardization",
        "Contact management setup",
        "Email template creation",
        "Document organization system"
      ]
    },
    {
      number: 3,
      title: "Basic Admin Handoff",
      description: "Transfer everyday administrative tasks to your dedicated VA for immediate time savings.",
      items: [
        "Email management delegation",
        "Calendar scheduling",
        "Meeting preparation",
        "Basic customer service tasks",
        "Travel arrangements"
      ]
    },
    {
      number: 4,
      title: "Advanced Task Delegation",
      description: "Gradually transition specialized and complex tasks to your experienced VA.",
      items: [
        "Transaction coordination",
        "Client onboarding processes",
        "Reporting and analytics",
        "Social media management",
        "Content creation assistance"
      ]
    },
    {
      number: 5,
      title: "Revenue-Boosting Activities",
      description: "Focus your newly freed time on high-value activities that grow your business.",
      items: [
        "Proactive client relationship management",
        "Strategic planning sessions",
        "Business development focus",
        "Expanded marketing efforts",
        "Professional development"
      ]
    }
  ];

  return (
    <section id="implementation" className="section bg-gradient-primary">
      <div className="container">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-primary-700 mb-4">The Smooth Staffing Method</h2>
          <p className="text-neutral-600 text-lg max-w-2xl mx-auto">
            Our proven 5-phase implementation ensures seamless integration of your VA into your business operations.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {phases.map((phase, index) => (
            <motion.div 
              key={index}
              className="reveal-on-scroll"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <PhaseCard
                number={phase.number}
                title={phase.title}
                description={phase.description}
                items={phase.items}
              />
            </motion.div>
          ))}
        </div>

        <div className="text-center">
          <motion.div
            className="relative inline-block"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <a href="#contact" className="btn btn-primary">
              Start Phase 1 Today →
            </a>
            
            {/* Decorative element */}
            <div className="absolute -right-10 -top-10 w-20 h-20">
              <svg 
                className="w-full h-full text-accent-500 opacity-20 animate-spin-slow"
                style={{ animationDuration: '15s' }}
                viewBox="0 0 100 100" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="M50,0 C77.6142375,0 100,22.3857625 100,50 C100,77.6142375 77.6142375,100 50,100 C22.3857625,100 0,77.6142375 0,50 C0,22.3857625 22.3857625,0 50,0 Z M50,10 C27.90861,10 10,27.90861 10,50 C10,72.09139 27.90861,90 50,90 C72.09139,90 90,72.09139 90,50 C90,27.90861 72.09139,10 50,10 Z" 
                  fill="currentColor"
                />
              </svg>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Implementation;